import { twMerge } from 'tailwind-merge';
import { Fragment, useMemo } from 'react';
import { getByPath } from '../lib/path';
import { applyTemplate } from '../lib/template';
import type {
  FieldConfig,
  FormModel,
  OptionNormalized,
  OptionsMapKeys,
  OptionsTemplate,
} from '../model/types';
import { FlowbiteControls } from './adapters/flowbite/controls';

type OptionsConfig = {
  options?: { mapKeys?: OptionsMapKeys; template?: OptionsTemplate };
};

function normalizeOptions(
  cfg: OptionsConfig,
  list: Record<string, unknown>[]
): OptionNormalized[] {
  const opts = cfg?.options;
  if (opts?.mapKeys) {
    const { labelKey, valueKey } = opts.mapKeys;
    return list.map((item) => ({
      label: String(item[labelKey] ?? ''),
      value: item[valueKey] as string | number,
    }));
  }
  if (opts?.template) {
    const tpl = opts.template;
    return list.map((item) => ({
      label: applyTemplate(tpl.label, item as Record<string, unknown>),
      value: applyTemplate(tpl.value, item as Record<string, unknown>),
    }));
  }
  return list.map((item) => {
    const o = item as Record<string, unknown>;
    return {
      label: String(o.label ?? ''),
      value: (o.value as string | number) ?? '',
    };
  });
}

export function FormControls<TCtx = unknown>({
  form,
  filter,
}: {
  form: FormModel<TCtx>;
  filter?: (cfg: FieldConfig<TCtx>) => boolean;
}) {
  const { controls } = form.config;
  return (
    <>
      {controls
        .filter((c) => (filter ? filter(c) : true))
        .map((cfg) => (
          <Fragment key={cfg.id}>
            <Field form={form} cfg={cfg} />
          </Fragment>
        ))}
    </>
  );
}

function Field<TCtx>({
  form,
  cfg,
}: {
  form: FormModel<TCtx>;
  cfg: FieldConfig<TCtx>;
}) {
  const { state, runtimeById, onChange, onBlur, ctx } = form;
  const runtime = runtimeById[cfg.id] || {};
  const value =
    state.values[cfg.id] ??
    (typeof cfg.read === 'function'
      ? cfg.read(ctx)
      : getByPath(ctx, cfg.read)) ??
    (cfg.type === 'checkbox' ? false : '');

  const hasError = state.errors[cfg.id];
  const isTouched = state.touched[cfg.id];
  const showError = hasError && (isTouched || state.submitCount > 0);
  const error = showError ? (state.errors[cfg.id] ?? null) : null;

  const options = useMemo(() => {
    if (cfg.type !== 'select') return undefined;
    const src = cfg.options?.source;
    const rawList =
      typeof src === 'function'
        ? (
            src as (ctx: TCtx) => (OptionNormalized | Record<string, unknown>)[]
          )(ctx)
        : Array.isArray(src)
          ? src
          : [];
    const list = Array.isArray(rawList) ? rawList : [];
    return normalizeOptions(cfg, list as Record<string, unknown>[]);
  }, [cfg, ctx]);

  if (runtime.hidden) return null;

  if (cfg.type === 'customHtml') {
    return (
      <div className="col-span-2">
        {cfg.template?.({ values: state.values, ctx })}
      </div>
    );
  }

  if (cfg.type === 'customControl') {
    const Comp = form.config._registry?.[cfg.component ?? ''];
    return (
      <div className="flex flex-col gap-1">
        {cfg.label && (
          <label className="text-sm">
            {cfg.label}
            {runtime.required && <span className="text-red-600"> *</span>}
          </label>
        )}
        {Comp ? (
          <Comp
            value={value}
            onChange={(v: unknown) => onChange(cfg.id, v)}
            onBlur={() => onBlur(cfg.id)}
            {...cfg.props}
          />
        ) : null}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  const flowbiteKey = cfg.type as keyof typeof FlowbiteControls;
  const Render = FlowbiteControls[flowbiteKey];
  if (!Render) return null;

  const props = cfg.props as Record<string, unknown> | undefined;
  const className =
    typeof props?.className === 'string' ? props.className : undefined;
  const placeholder =
    typeof props?.placeholder === 'string' ? props.placeholder : undefined;
  const label = typeof props?.label === 'string' ? props.label : undefined;
  const onChangeProp = props?.onChange;
  const renderOuterLabel = cfg.type !== 'checkbox' && cfg.type !== 'fileUpload';
  const labelForControl = cfg.type === 'checkbox' ? cfg.label : label;

  return (
    <div className={twMerge('flex flex-col gap-1', className)}>
      {renderOuterLabel && cfg.label && (
        <label className="text-sm">
          {cfg.label}
          {runtime.required && <span className="text-red-600"> *</span>}
        </label>
      )}
      <Render
        value={value}
        onChange={(v: unknown) => {
          if (typeof onChangeProp === 'function') {
            (onChangeProp as (next: unknown) => void)(v);
          }
          onChange(cfg.id, v);
        }}
        onBlur={() => onBlur(cfg.id)}
        options={options}
        props={{
          disabled: runtime.disabled,
          readOnly: runtime.readOnly,
          required: runtime.required,
          placeholder,
          label: labelForControl,
          id: cfg.id,
        }}
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      {cfg.helperText && (
        <p className="text-xs text-gray-500">{cfg.helperText}</p>
      )}
    </div>
  );
}
